import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import { useAuthStore } from "@/stores/auth-store";

// NEXT_PUBLIC_API_URL puede venir con o sin el sufijo `/api/v1`. Cualquiera de
// estos formatos funciona:
//   `https://shazon.exedra.space/api/v1`  (con sufijo, lo respetamos)
//   `https://shazon.exedra.space`         (sin sufijo, lo añadimos)
//   `/api/v1`                              (relativo detrás de nginx)
//   ""  (vacío) → `/api/v1` relativo por defecto
// Antes este código añadía SIEMPRE `/api/v1` al BASE_URL, lo que duplicaba
// el path cuando el env ya lo incluía.
const BASE_URL = process.env.NEXT_PUBLIC_API_URL || "";
const API_URL = BASE_URL
  ? (BASE_URL.replace(/\/$/, "").endsWith("/api/v1")
      ? BASE_URL.replace(/\/$/, "")
      : `${BASE_URL.replace(/\/$/, "")}/api/v1`)
  : "/api/v1";


export const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Token refresh state management
let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

const subscribeTokenRefresh = (callback: (token: string) => void) => {
  refreshSubscribers.push(callback);
};

const onTokenRefreshed = (token: string) => {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
};

const onRefreshFailed = () => {
  refreshSubscribers = [];
};

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = useAuthStore.getState().accessToken;
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for token refresh with concurrent request handling
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // Only handle 401 for token expiration, not for other auth errors
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't retry if this is the login or refresh endpoint
      if (originalRequest.url?.includes('/auth/login') || originalRequest.url?.includes('/auth/refresh')) {
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      // If a refresh is already in progress, wait for it to complete
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh((newToken: string) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
            }
            resolve(apiClient(originalRequest));
          });
        });
      }

      const refreshToken = useAuthStore.getState().refreshToken;

      // Only try to refresh if we have a refresh token
      if (refreshToken) {
        isRefreshing = true;

        try {
          const response = await axios.post(`${API_URL}/auth/refresh`, {
            refresh_token: refreshToken,
          });

          const { access_token, refresh_token } = response.data;

          useAuthStore.getState().setTokens(access_token, refresh_token);

          // Notify all waiting requests
          onTokenRefreshed(access_token);

          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${access_token}`;
          }

          isRefreshing = false;

          // Retry the original request
          return apiClient(originalRequest);
        } catch (refreshError) {
          isRefreshing = false;
          onRefreshFailed();

          // Refresh token caducado/invalido → sesion finalizada. Es flujo esperado al
          // arrancar con un token viejo en localStorage; no spameamos console.error.
          const refreshStatus = (refreshError as AxiosError)?.response?.status;
          if (refreshStatus !== 401) {
            console.warn('Token refresh failed:', refreshError);
          }
          useAuthStore.getState().logout();
          if (typeof window !== "undefined") {
            window.location.href = "/login";
          }
          return Promise.reject(refreshError);
        }
      } else {
        // No refresh token available - user needs to login
        useAuthStore.getState().logout();
        if (typeof window !== "undefined") {
          window.location.href = "/login";
        }
        return Promise.reject(error);
      }
    }

    // For 403 or other errors, just reject without logging out
    return Promise.reject(error);
  }
);

// Helper to convert snake_case to camelCase
export const toCamelCase = (obj: unknown): unknown => {
  if (Array.isArray(obj)) {
    return obj.map(toCamelCase);
  }
  if (obj !== null && typeof obj === "object") {
    return Object.keys(obj).reduce((result, key) => {
      const camelKey = key.replace(/_([a-z])/g, (_, letter) =>
        letter.toUpperCase()
      );
      (result as Record<string, unknown>)[camelKey] = toCamelCase(
        (obj as Record<string, unknown>)[key]
      );
      return result;
    }, {} as Record<string, unknown>);
  }
  return obj;
};

// Helper to convert camelCase to snake_case
export const toSnakeCase = (obj: unknown): unknown => {
  if (Array.isArray(obj)) {
    return obj.map(toSnakeCase);
  }
  if (obj !== null && typeof obj === "object") {
    return Object.keys(obj).reduce((result, key) => {
      const snakeKey = key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
      (result as Record<string, unknown>)[snakeKey] = toSnakeCase(
        (obj as Record<string, unknown>)[key]
      );
      return result;
    }, {} as Record<string, unknown>);
  }
  return obj;
};
